import { useContext, useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../../i18n/index.js';
import { playSound } from '../../audio/sounds.js';
import { GameOverBanner, GameOverContext } from '../common/TurnStatus.js';
import { PIECE_SVG } from './pieces.js';
import {
  FILES,
  boardFromFen,
  capturedPieces,
  squareName,
  type ChessColor,
  type ChessPiece,
  type ChessPieceType,
  type ChessPromotion,
  type ChessView,
} from '../../../shared/games/chess/index.js';
import type { ArenaResult, GamePlayerView, GameStateView } from '../../../shared/protocol.js';
import type { ArenaSocket } from '../../realtime.js';

export type ChessTheme = 'wood' | 'dark';
const THEME_KEY = 'areena.chessTheme';

/** Board look (decision 2026-09-30: classic wood or Areena dark), chosen in the lobby. */
export function useChessTheme(): [ChessTheme, (t: ChessTheme) => void] {
  const [theme, setTheme] = useState<ChessTheme>(() => {
    try {
      return localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'wood';
    } catch {
      return 'wood';
    }
  });
  const choose = (next: ChessTheme) => {
    setTheme(next);
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      // Not stored (private window).
    }
  };
  return [theme, choose];
}

export function PieceIcon(props: { color: ChessColor; type: ChessPieceType; class?: string }) {
  return (
    <svg
      class={`chess-piece ${props.class ?? ''}`}
      viewBox="0 0 45 45"
      aria-hidden="true"
      // The markup comes from the fixed piece set in pieces.ts, never from user input.
      dangerouslySetInnerHTML={{ __html: PIECE_SVG[`${props.color}${props.type}`] ?? '' }}
    />
  );
}

const clockText = (ms: number) => {
  const total = Math.max(0, ms);
  if (total < 20_000) return `0:${(total / 1000).toFixed(1).padStart(4, '0')}`;
  const s = Math.ceil(total / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** Remaining time of a player: the running clock counts down from the start of the turn. */
function remaining(state: GameStateView, p: GamePlayerView, serverNow: number): number {
  const running = state.turn?.userId === p.id;
  return running ? p.timeBankMs - (serverNow - state.turn!.startedAt) : p.timeBankMs;
}

interface LastMoveData {
  from: string;
  to: string;
  san: string;
  capture: boolean;
  check: boolean;
}

/** Chess UI (decision 2026-09-30): board, chess clocks, captured pieces, move list and draw offers. */
export function ChessGame(props: { state: GameStateView<ChessView>; socket: ArenaSocket; serverNow: number }) {
  const { state, socket, serverNow } = props;
  const view = state.module;
  const over = useContext(GameOverContext);
  const [theme] = useChessTheme();
  const myColor = view.myColor ?? 'w';
  const flipped = myColor === 'b';
  const myTurn = !over && state.turn?.userId === state.me.id;
  const [selected, setSelected] = useState<string | null>(null);
  const [promotion, setPromotion] = useState<{ from: string; to: string } | null>(null);
  const [pending, setPending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [drag, setDrag] = useState<{ from: string; x: number; y: number } | null>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  /** The square picked by the last pointer down: the click that follows must not unselect it. */
  const justPicked = useRef<string | null>(null);

  const board = boardFromFen(view.fen);
  const pieceAt = (sq: string): ChessPiece | null => {
    const file = FILES.indexOf(sq[0]!);
    const rank = Number(sq[1]);
    return board[(8 - rank) * 8 + file] ?? null;
  };
  const targets = new Set(selected && myTurn ? (view.legal[selected] ?? []) : []);
  const last = state.lastMove?.data as LastMoveData | undefined;

  // Sounds for every new move; the selection is cleared when the position changes.
  const lastKey = `${view.history.length}`;
  const first = useRef(true);
  useEffect(() => {
    setSelected(null);
    setPromotion(null);
    if (!first.current && last) playSound(last.check ? 'chessCheck' : last.capture ? 'chessCapture' : 'chessMove');
    if (!first.current && state.turn?.userId === state.me.id) playSound('turn', 250);
    first.current = false;
  }, [lastKey]);

  // Draw offers: the notice when the opponent declined my offer.
  const prevOffer = useRef(state.drawOffer);
  useEffect(() => {
    const before = prevOffer.current;
    prevOffer.current = state.drawOffer;
    if (before?.byUserId === state.me.id && state.drawOffer === null && !over) setNotice(t.chess.drawDeclined);
    if (state.drawOffer && state.drawOffer.byUserId !== state.me.id) playSound('chatPrivate');
  }, [state.drawOffer?.byUserId ?? 0]);

  // Low time: a tick every second in the last ten seconds of my clock.
  const myLeft = remaining(state, state.me, serverNow);
  const lastTick = useRef(0);
  useEffect(() => {
    if (!myTurn || myLeft > 10_000 || myLeft <= 0) return;
    const s = Math.ceil(myLeft / 1000);
    if (s !== lastTick.current) {
      lastTick.current = s;
      playSound('bankTick');
    }
  }, [Math.ceil(myLeft / 1000), myTurn]);

  const send = (from: string, to: string, promo?: ChessPromotion) => {
    setPending(true);
    setNotice(null);
    socket.emit('game:move', { from, to, promotion: promo }, (r: ArenaResult) => {
      setPending(false);
      if (!r.ok) setNotice(t.arena.errors[r.error]);
    });
    setSelected(null);
  };

  const tryMove = (from: string, to: string) => {
    if (!(view.legal[from] ?? []).includes(to)) return false;
    const piece = pieceAt(from);
    if (piece?.type === 'p' && (to[1] === '8' || to[1] === '1')) setPromotion({ from, to });
    else send(from, to);
    return true;
  };

  const canPick = (sq: string) => myTurn && !pending && (view.legal[sq]?.length ?? 0) > 0;

  const clickSquare = (sq: string) => {
    if (justPicked.current === sq) {
      justPicked.current = null;
      return;
    }
    justPicked.current = null;
    if (!myTurn || pending) return;
    if (selected && targets.has(sq)) {
      tryMove(selected, sq);
      return;
    }
    setSelected(canPick(sq) && selected !== sq ? sq : null);
  };

  const squareFromPoint = (x: number, y: number): string | null => {
    const rect = boardRef.current?.getBoundingClientRect();
    if (!rect || x < rect.left || y < rect.top || x >= rect.right || y >= rect.bottom) return null;
    let col = Math.floor(((x - rect.left) / rect.width) * 8);
    let row = Math.floor(((y - rect.top) / rect.height) * 8);
    if (flipped) {
      col = 7 - col;
      row = 7 - row;
    }
    return squareName(row * 8 + col);
  };

  // Dragging a piece: pointer down on an own piece, release on a target square.
  const onPointerDown = (e: PointerEvent, sq: string) => {
    if (!canPick(sq) || e.button !== 0) return;
    e.preventDefault();
    setSelected(sq);
    justPicked.current = sq;
    setDrag({ from: sq, x: e.clientX, y: e.clientY });
  };
  useEffect(() => {
    if (!drag) return;
    const move = (e: PointerEvent) => setDrag((d) => (d ? { ...d, x: e.clientX, y: e.clientY } : d));
    const up = (e: PointerEvent) => {
      const to = squareFromPoint(e.clientX, e.clientY);
      const from = drag.from;
      setDrag(null);
      if (to && to !== from) tryMove(from, to);
    };
    addEventListener('pointermove', move);
    addEventListener('pointerup', up, { once: true });
    return () => {
      removeEventListener('pointermove', move);
      removeEventListener('pointerup', up);
    };
  }, [drag?.from]);

  const offerDraw = () => {
    setNotice(null);
    socket.emit('game:drawOffer', (r: ArenaResult<{ declined?: boolean }>) => {
      if (!r.ok) setNotice(t.arena.errors[r.error]);
      else if (r.declined) setNotice(t.chess.drawDeclinedByAi);
    });
  };
  const respondDraw = (accept: boolean) =>
    socket.emit('game:drawRespond', { accept }, (r: ArenaResult) => {
      if (!r.ok) setNotice(t.arena.errors[r.error]);
    });

  // King in check: highlighted.
  const checkSquare = view.inCheck
    ? (() => {
        const i = board.findIndex((p) => p?.type === 'k' && p.color === view.toMove);
        return i >= 0 ? squareName(i) : null;
      })()
    : null;

  const squares = Array.from({ length: 64 }, (_, i) => (flipped ? 63 - i : i));
  const cellPx = boardRef.current ? boardRef.current.getBoundingClientRect().width / 8 : 60;
  const captured = capturedPieces(board);
  const colorName = (c: ChessColor) => (c === 'w' ? t.chess.white : t.chess.black);
  const opponentColor: ChessColor = myColor === 'w' ? 'b' : 'w';

  const playerBox = (p: GamePlayerView, color: ChessColor) => {
    const left = remaining(state, p, serverNow);
    const running = !over && state.turn?.userId === p.id;
    const taken = color === 'w' ? captured.byWhite : captured.byBlack;
    const lead = color === 'w' ? captured.whiteLead : -captured.whiteLead;
    return (
      <div class={`chess-player ${running ? 'running' : ''}`}>
        <div class="chess-player-name">
          <PieceIcon color={color} type="k" class="chess-player-king" />
          <span>{p.id === state.me.id ? t.snake.you : p.displayName}</span>
        </div>
        <div class={`chess-clock ${running ? 'running' : ''} ${left < 30_000 ? 'low' : ''}`} aria-label={t.chess.clock}>
          {clockText(left)}
        </div>
        <div class="chess-captured" aria-hidden="true">
          {taken.map((type) => (
            <PieceIcon color={color === 'w' ? 'b' : 'w'} type={type} />
          ))}
          {lead > 0 && <span class="chess-lead">+{lead}</span>}
        </div>
      </div>
    );
  };

  const pairs: [string, string | undefined][] = [];
  for (let i = 0; i < view.history.length; i += 2) pairs.push([view.history[i]!, view.history[i + 1]]);
  const incoming = !over && state.drawOffer && state.drawOffer.byUserId !== state.me.id;
  const myOffer = !over && state.drawOffer?.byUserId === state.me.id;

  return (
    <div class="chess">
      {over ? (
        <GameOverBanner over={over} />
      ) : (
        <div class={`turn-banner ${myTurn ? 'mine' : 'theirs'}`} role="status">
          <div class="turn-text">
            {myTurn ? t.chess.yourMove : t.chess.theirMove(state.opponent.displayName)}
            {view.inCheck && <span class="chess-check-note">{t.chess.check}</span>}
          </div>
        </div>
      )}
      {!over && !state.opponent.online && <div class="conn-banner">{t.battleship.opponentOffline}</div>}
      {incoming && (
        <div class="chess-draw-offer" role="alert">
          <span>🤝 {t.chess.drawIncoming(state.opponent.displayName)}</span>
          <button class="btn btn-primary btn-small" onClick={() => respondDraw(true)}>
            {t.chess.drawAccept}
          </button>
          <button class="btn btn-small" onClick={() => respondDraw(false)}>
            {t.chess.drawDecline}
          </button>
        </div>
      )}
      {notice && <p class="muted chess-notice">{notice}</p>}

      <div class="chess-layout">
        <div class={`chess-board-wrap chess-${theme}`}>
          <div
            ref={boardRef}
            class={`chess-board ${myTurn && !pending ? 'active' : ''}`}
            role="grid"
            aria-label={t.chess.boardLabel}
          >
            {squares.map((i) => {
              const sq = squareName(i);
              const piece = board[i];
              const dark = (Math.floor(i / 8) + (i % 8)) % 2 === 1;
              const isTarget = targets.has(sq);
              const isLast = last && (last.from === sq || last.to === sq);
              const dragging = drag?.from === sq;
              const row = Math.floor(i / 8);
              const col = i % 8;
              const showRank = flipped ? col === 7 : col === 0;
              const showFile = flipped ? row === 0 : row === 7;
              return (
                <button
                  type="button"
                  class={`chess-sq ${dark ? 'dark' : 'light'} ${selected === sq ? 'selected' : ''} ${isLast ? 'last' : ''} ${checkSquare === sq ? 'check' : ''} ${isTarget ? (piece ? 'target capture' : 'target') : ''}`}
                  aria-label={`${t.chess.square(sq)}${piece ? `: ${colorName(piece.color)} ${t.chess.pieces[piece.type]}` : ''}`}
                  onClick={() => clickSquare(sq)}
                  onPointerDown={(e) => onPointerDown(e as unknown as PointerEvent, sq)}
                >
                  {showRank && <span class="chess-coord rank">{sq[1]}</span>}
                  {showFile && <span class="chess-coord file">{sq[0]}</span>}
                  {piece && <PieceIcon color={piece.color} type={piece.type} class={dragging ? 'lifted' : ''} />}
                </button>
              );
            })}
          </div>
          {drag && (
            <div class="chess-drag" style={{ left: `${drag.x - cellPx / 2}px`, top: `${drag.y - cellPx / 2}px`, width: `${cellPx}px`, height: `${cellPx}px` }}>
              {(() => {
                const p = pieceAt(drag.from);
                return p ? <PieceIcon color={p.color} type={p.type} /> : null;
              })()}
            </div>
          )}
        </div>

        <aside class="card chess-side">
          {playerBox(state.opponent, opponentColor)}
          <div class="chess-moves">
            <div class="chess-moves-title">{t.chess.moves}</div>
            {pairs.length === 0 ? (
              <p class="muted">{t.chess.noMoves}</p>
            ) : (
              <ol class="chess-move-list">
                {pairs.map(([w, b], n) => (
                  <li key={n}>
                    <span class="chess-move-no">{n + 1}.</span>
                    <span>{w}</span>
                    <span>{b ?? ''}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
          {playerBox(state.me, myColor)}
          <p class="muted chess-you-play">
            {t.chess.youPlay(colorName(myColor))} · {t.chess.clockLabel(Math.round(state.settings.timeBankMs / 60000), Math.round(state.settings.incrementMs / 1000))}
          </p>
          {!over && (
            <button class="btn btn-small" disabled={Boolean(state.drawOffer)} onClick={offerDraw}>
              🤝 {myOffer ? t.chess.drawOffered : t.chess.offerDraw}
            </button>
          )}
        </aside>
      </div>

      {promotion && (
        <div class="overlay" role="presentation" onClick={() => setPromotion(null)}>
          <div class="dialog card chess-promotion" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <h3>{t.chess.promoteTitle}</h3>
            <div class="chess-promotion-choices">
              {(['q', 'r', 'b', 'n'] as const).map((type) => (
                <button
                  class={`btn chess-${theme}`}
                  aria-label={t.chess.pieces[type]}
                  onClick={() => {
                    const p = promotion;
                    setPromotion(null);
                    send(p.from, p.to, type);
                  }}
                >
                  <PieceIcon color={myColor} type={type} />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
